import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import { COUNTRY_CODE, PLAYER_DEFAULT_VALUES, REGIONS } from "../constants";
import { useEmbedParametersContext } from "../contexts/EmbedParametersContext";
import { MediaContext } from "../contexts/MediaContext";
import { State } from "../enums";
import { isYleEmbed } from "../helpers/commonUtils";
import {
  isAudio,
  isLivePlayer,
  resolvePlayerType
} from "../helpers/previewHelpers";
import { useHistory } from "../hooks/useHistory/useHistory";
import { tunnusExists } from "../hooks/useLoginData/useLoginData";
import { usePlayerExternalApiEvents } from "../hooks/usePlayerExternalApiEvents/usePlayerExternalApiEvents";
import { getAreenaWatchedHistoryData } from "../service/areena-history";
import { fetchMedia, MediaData } from "../service/player-api";
import { getStoreStateOnMediaChanged } from "../store/parseStoreValues";
import { usePlayerStore } from "../store/usePlayerStore";
import { Player, SetPlayerApiFn, UpdateProps } from "../types/common";

import { PreviewAudio } from "./YleAudioPlayer/PreviewAudio/PreviewAudio";
import { PreviewVideo } from "./PreviewVideo";

const notAvailableWarn = (name: keyof Player) => () =>
  console.warn(`${name}() is not available in current player state"`);

interface Props {
  onPlayerExternalApiReady: (player: Player) => void;
}

export const PreviewContainer: FC<Props> = ({ onPlayerExternalApiReady }) => {
  const {
    getState: getPlayerStoreState,
    setState: setPlayerStoreState,
    useStoreState
  } = usePlayerStore();
  const externalEvents = useStoreState.externalEvents();
  const loginData = useStoreState.loginData();
  const geolocation = useStoreState.geolocation();

  const [mediaData, setMediaData] = useState<MediaData>();
  const [state, setState] = useState<State>();

  const playerInitialSetupDone = useRef(false);

  const playerReadyCalledRef = useRef(false);

  const attributes = useEmbedParametersContext();
  useHistory({ hasError: state === State.ERROR });
  usePlayerExternalApiEvents();

  const setup = useCallback(
    async (updateProps?: UpdateProps): Promise<void> => {
      try {
        const storeState = getPlayerStoreState();
        storeState.setMediaLoadingStartTimestamp(Date.now());

        const embedId = updateProps?.programId ?? storeState.embedId;

        if (!embedId) {
          throw new Error("EmbedId is missing");
        }

        const { data: fetchedData, state: fetchedState } = await fetchMedia({
          attributes,
          embedId,
          geolocation: storeState.geolocation,
          isMobile: getPlayerStoreState().device.isMobile
        });

        const playerType = resolvePlayerType(
          fetchedState,
          fetchedData.serviceId
        );

        const userData =
          attributes.playbackHistory &&
          !isLivePlayer(playerType) &&
          storeState.loginData.loggedIn &&
          isYleEmbed() &&
          fetchedData.programId
            ? await getAreenaWatchedHistoryData(fetchedData.programId)
            : {};

        if (embedId !== storeState.embedId) {
          // Set these values to store only if embedId has been changed, i.e. media has changed
          setPlayerStoreState(
            getStoreStateOnMediaChanged({
              aspectRatio: updateProps?.aspectRatio,
              data: fetchedData,
              embedId,
              embedParams: attributes,
              isAutoplay: updateProps?.autoplay ?? storeState.isAutoplay,
              isFirstTimeRender: !storeState.programId,
              playerType,
              state: fetchedState,
              userData
            }),
            false,
            "setPlayerStoreInitialsByMediaData"
          );
        } else {
          // If media is not changed, update only the media data
          // i.e. on serviceSchedule update
          setPlayerStoreState({ media: fetchedData }, false, "setMediaData");
        }

        setMediaData({ data: fetchedData, state: fetchedState });
      } catch (err) {
        setState(State.ERROR);
        getPlayerStoreState().setError(err);
        console.error("Player error", err);
      }
    },
    [attributes, getPlayerStoreState, setPlayerStoreState]
  );

  useEffect(() => {
    if (!playerInitialSetupDone.current) {
      if (!attributes.id) {
        throw new Error("Id is missing");
      }
      playerInitialSetupDone.current = true;
      void setup({ programId: attributes.id });
    }
  }, [attributes, playerInitialSetupDone, setup]);

  useEffect(() => {
    if (mediaData && state !== State.ERROR) {
      const { data: fetchedData, state: fetchedState } = mediaData;
      const isUserInFinland = geolocation.countryCode === COUNTRY_CODE.FI;
      const isAlwaysAllowed = fetchedData.region === REGIONS.WORLD;
      const isIdentifiedUser = loginData.residenceInFinland;
      const isChannel = fetchedState === State.ONGOING_CHANNEL;
      const isPortabilityRegion = geolocation.isPortabilityRegion;

      if (
        isAlwaysAllowed ||
        isUserInFinland ||
        isChannel ||
        (isIdentifiedUser && isPortabilityRegion) ||
        (isPortabilityRegion && fetchedState === State.GONE) ||
        fetchedState === State.SRC_AUDIO
      ) {
        setState(fetchedState);
      } else if (isPortabilityRegion && tunnusExists()) {
        setState(State.IDENTIFICATION);
      } else {
        setState(State.BLOCKED);
      }
    }
  }, [geolocation, loginData, mediaData, state]);

  const setPlayerApi: SetPlayerApiFn = useCallback(
    playerExternalApi => {
      onPlayerExternalApiReady({
        ...playerExternalApi,
        update: setup
      });
    },
    [onPlayerExternalApiReady, setup]
  );

  const setPlayerApiSimple = useCallback(() => {
    if (!playerReadyCalledRef.current && externalEvents) {
      playerReadyCalledRef.current = true;
      setPlayerApi({
        closeFullscreen: notAvailableWarn("closeFullscreen"),
        defaultParameters: () => PLAYER_DEFAULT_VALUES,
        destroy: notAvailableWarn("destroy"),
        focus: notAvailableWarn("focus"),
        hideChatButton: notAvailableWarn("hideChatButton"),
        hideControls: notAvailableWarn("hideControls"),
        mute: notAvailableWarn("mute"),
        muted: () => false,
        on: (eventName, callback) =>
          externalEvents.addListener(eventName, callback),
        openFullscreen: notAvailableWarn("openFullscreen"),
        pause: notAvailableWarn("pause"),
        play: async () => notAvailableWarn("play")(),
        playbackRates: () => [],
        seek: notAvailableWarn("seek"),
        setAspectRatio: notAvailableWarn("setAspectRatio"),
        setPlaybackRate: notAvailableWarn("setPlaybackRate"),
        showChatButton: notAvailableWarn("showChatButton"),
        showControls: notAvailableWarn("showControls"),
        startLoad: notAvailableWarn("startLoad"),
        stopLoad: notAvailableWarn("stopLoad"),
        unmute: notAvailableWarn("unmute"),
        volume: () => 1
      });
    }
  }, [externalEvents, setPlayerApi]);

  if (state) {
    return (
      <MediaContext.Provider value={mediaData?.data}>
        {attributes.yleAudioPlayer || isAudio(mediaData?.data.contentType) ? (
          <PreviewAudio
            setPlayerApiSimple={setPlayerApiSimple}
            setPlayerApi={setPlayerApi}
            state={state}
          />
        ) : (
          <PreviewVideo
            refreshComponent={setup}
            setPlayerApi={setPlayerApi}
            setPlayerApiSimple={setPlayerApiSimple}
            state={state}
            update={setup}
          />
        )}
      </MediaContext.Provider>
    );
  }
  return null;
};
